/**
 * Offer banner settings.
 * Set OFFER_END_ISO when running a timed promotion (ISO 8601 UTC).
 * Example: Sunday 22 June 2026 23:59:59 IST → "2026-06-22T18:29:59.000Z"
 * If that date has passed, the timer uses the end of the current week (Sunday 23:59:59 IST).
 */

export const OFFER_END_ISO = "2026-03-31T18:29:59.000Z";

export const OFFER_COPY = {
  badge: "No payment required",
  heading: "Your First Demo Class is",
  highlight: "100% FREE",
  headingSuffix: "",
  description:
    "Try a live language class with our expert trainers. Pick Japanese, German, French, or Korean — book in under a minute.",
  expiredMessage:
    "Enrollment for this period has closed. You can still book your free demo class anytime.",
};

const IST = "Asia/Kolkata";
const WEEKDAY_MAP = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

function getISTYMD(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-IN", {
    timeZone: IST,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(date);

  return {
    year: Number(parts.find((part) => part.type === "year").value),
    month: Number(parts.find((part) => part.type === "month").value),
    day: Number(parts.find((part) => part.type === "day").value),
  };
}

function getISTWeekday(date = new Date()) {
  const weekday = new Intl.DateTimeFormat("en-US", {
    timeZone: IST,
    weekday: "short",
  }).format(date);

  return WEEKDAY_MAP[weekday];
}

/** 23:59:59 IST on a given calendar day. */
function getEndOfDayIST(year, month, day) {
  return new Date(Date.UTC(year, month - 1, day, 18, 29, 59, 999));
}

/** Next Sunday 23:59:59 IST from the given moment (inclusive of the current week). */
export function getEndOfWeekIST(fromDate = new Date()) {
  let probe = new Date(fromDate);

  for (let i = 0; i < 8; i += 1) {
    const { year, month, day } = getISTYMD(probe);

    if (getISTWeekday(probe) === 0) {
      const end = getEndOfDayIST(year, month, day);
      if (end.getTime() > Date.now()) {
        return end;
      }
    }

    probe = new Date(probe.getTime() + 24 * 60 * 60 * 1000);
  }

  return getEndOfWeekIST(new Date(fromDate.getTime() + 7 * 24 * 60 * 60 * 1000));
}

export function getActiveOfferEndDate() {
  const configured = new Date(OFFER_END_ISO);
  if (!Number.isNaN(configured.getTime()) && configured.getTime() > Date.now()) {
    return configured;
  }

  return getEndOfWeekIST();
}

export function getTimeRemaining(endDate) {
  const totalMs = Math.max(0, endDate.getTime() - Date.now());

  return {
    expired: totalMs === 0,
    days: Math.floor(totalMs / (1000 * 60 * 60 * 24)),
    hours: Math.floor((totalMs / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((totalMs / (1000 * 60)) % 60),
    seconds: Math.floor((totalMs / 1000) % 60),
  };
}

export function formatOfferEndLabel(endDate) {
  return endDate.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: IST,
  });
}
